import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { create } from "api/RequestService";
import CloseIcon from "@mui/icons-material/Close";
import { useColorTheme } from "context/ColorThemeContext";

function CreateRequest({ open, onClose, initialData, refreshDataAfterChange }) {
  const colors = useColorTheme();
  const [form, setForm] = useState({ status: 0, requestComment: "", phoneNumber: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setForm({
      status: initialData?.status || 0,
      requestComment: initialData?.requestComment || "",
      phoneNumber: user?.phone || "",
    });
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!form.phoneNumber || form.phoneNumber.trim() === "") {
      newErrors.phoneNumber = "Số điện thoại không được để trống";
    } else if (!/^(0|\+84)[0-9]{9}$/.test(form.phoneNumber)) {
      newErrors.phoneNumber = "Số điện thoại không hợp lệ";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      customerDesignId: initialData.id,
      requestComment: form.requestComment || "",
      phone: form.phoneNumber || "",
    };
    setLoading(true);
    const result = await create(payload);

    if (result.status === 200) {
      Swal.fire({
        icon: "success",
        title: "Gửi yêu cầu thành công",
        showConfirmButton: false,
        timer: 1500,
      });
      const dataUpdate = {
        status: result.data?.status,
        resultComment: result.data?.resultComment || "",
      };
      refreshDataAfterChange(dataUpdate);
      setForm({ status: 0, resultComment: "" });
      onClose();
      setLoading(false);
    } else {
      Swal.fire({
        icon: "error",
        title: result.status.data,
        showConfirmButton: false,
        timer: 1500,
      });
    }
    refreshDataAfterChange();
    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px", // Bo góc giống hình
          p: 3, // Padding tổng thể
          background: colors.popupBackground || "#f2f2f2",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center", // Căn giữa tiêu đề
          fontWeight: "bold",
          fontSize: "24px",
          pb: 0,
          mt: 2,
        }}
      >
        GỬI YÊU CẦU
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#000",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography fontWeight="bold" fontSize="16px" mb={0.5} color="text.primary">
          Số điện thoại{" "}
          <Typography component="span" color="error">
            *
          </Typography>
        </Typography>
        <TextField
          variant="outlined"
          name="phoneNumber"
          value={form.phoneNumber}
          placeholder="Nhập số điện thoại..."
          fullWidth
          onChange={handleChange}
          error={Boolean(errors.phoneNumber)}
          helperText={errors.phoneNumber}
          sx={{
            mb: 2,
            borderRadius: "8px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />

        <Typography fontWeight="bold" fontSize="16px" mb={0.5} color="text.primary">
          Nhập mô tả
        </Typography>
        <TextField
          variant="outlined"
          name="requestComment"
          value={form.requestComment}
          placeholder="Nhập ghi chú..."
          fullWidth
          multiline
          rows={20}
          onChange={handleChange}
          sx={{
            borderRadius: "8px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center" }}>
        <Button variant="contained" size="small" onClick={handleSave}>
          Gửi yêu cầu
        </Button>
      </DialogActions>
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

CreateRequest.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.number,
    requestComment: PropTypes.string,
  }),
  refreshDataAfterChange: PropTypes.func.isRequired,
};

CreateRequest.defaultProps = {
  initialData: null,
};

export default CreateRequest;
